import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { getAutomationsList } from '@/features/air/automation-settings/api'
import { getCaseList } from '@/features/air/case-management/api'
import { getPolicyList } from '@/features/air/policy-management/api'

import { fingerprint, successStaleTime } from '@/shared/api/utils/query'
import { time } from '@/shared/datetime/utils/time'

import type { MultiTenantAutomationResponse } from './types'

/**
 * Query keys for multi-tenant views (separate namespace to avoid cache collisions).
 *
 * `tenants` is fingerprinted rather than embedded raw: an MSP-scoped selection
 * can carry many opaque tenant IDs, which bloat the cache and lag the Query
 * devtools. The content hash still busts cache when the selection changes.
 */
export const multiTenantKeys = {
	all: ['multi-tenant'] as const,
	cases: (tenants?: string[], page?: number, limit?: number) =>
		[
			...multiTenantKeys.all,
			'cases',
			{ tenants: fingerprint(tenants), page, limit },
		] as const,
	policies: (tenants?: string[]) =>
		[
			...multiTenantKeys.all,
			'policies',
			{ tenants: fingerprint(tenants) },
		] as const,
	automations: (tenants?: string[]) =>
		[
			...multiTenantKeys.all,
			'automations',
			{ tenants: fingerprint(tenants) },
		] as const,
}

/**
 * Query options for multi-tenant data fetching.
 * Uses tenant="all" path param + tenants array for X-AIR-Request-Tenants header.
 */
export const multiTenantQueries = {
	cases: (tenants?: string[], page: number = 1, limit: number = 25) =>
		queryOptions({
			queryKey: multiTenantKeys.cases(tenants, page, limit),
			queryFn: ({ signal }) =>
				getCaseList('all', limit, page, undefined, undefined, signal, tenants),
			staleTime: ({ state }) => successStaleTime(state, 30, 's'),
			gcTime: time(1, 'm'),
		}),
	policies: (tenants?: string[]) =>
		queryOptions({
			queryKey: multiTenantKeys.policies(tenants),
			queryFn: ({ signal }) => getPolicyList('all', signal, tenants),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			gcTime: time(3, 'm'),
			select: (data) => data.rules,
		}),
	automations: (tenants?: string[]) =>
		queryOptions({
			queryKey: multiTenantKeys.automations(tenants),
			queryFn: ({ signal }) =>
				getAutomationsList(
					'all',
					signal,
					undefined,
					undefined,
					undefined,
					tenants,
				),
			staleTime: ({ state }) => successStaleTime(state, 2, 'm'),
			gcTime: time(3, 'm'),
			// The multi-tenant endpoint augments each automation with the owning
			// `tenant` (the shared single-tenant `AutomationResponse` type omits it).
			// Assert that contract here so consumers receive properly-typed rows.
			select: (data) => data.responses as Array<MultiTenantAutomationResponse>,
		}),
}

/**
 * Fetch cases across all tenants with server-side pagination
 * @param tenants - optional tenant IDs to filter by
 * @param page - page number (default 1)
 * @param limit - items per page (default 25)
 */
export const useMultiTenantCases = (
	tenants?: string[],
	page: number = 1,
	limit: number = 25,
) => {
	return useSuspenseQuery(multiTenantQueries.cases(tenants, page, limit))
}

/**
 * Fetch policies across all tenants (selects rules from response)
 * @param tenants - optional tenant IDs to filter by
 */
export const useMultiTenantPolicies = (tenants?: string[]) => {
	return useSuspenseQuery(multiTenantQueries.policies(tenants))
}

/**
 * Fetch automations across all tenants
 * @param tenants - optional tenant IDs to filter by
 */
export const useMultiTenantAutomations = (tenants?: string[]) => {
	return useSuspenseQuery(multiTenantQueries.automations(tenants))
}
